import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import dayjs from 'shared/helpers/dayjs';
import routesConfig from 'constants/routesConfig.json';
import useWindowSize from 'shared/hooks/useWindowResize';
import breakpoints from 'theme/atoms/breakpoints';
import ErrorBoundary from 'layout/components/ErrorBoundary';
import { TopBar } from 'topBar/components/TopBar';
import { NoteCaptureSection } from 'shiftNotes/components/NoteCapture/_index';
import {
  ShiftNoteCaptureProvider,
  useShiftNoteCapture,
} from 'shiftNotes/helpers/ShiftNoteCaptureProvider';
import DateSearchPill from 'events/components/EventsHome/DateSearchPill';
import ActivityList from 'events/components/EventsHome/ActivityList';
import EventsHomeLeftColumn from 'events/components/EventsHome/EventsHomeLeftColumn';
import EventsRightColumn from 'events/components/EventsHome/EventsRightColumn';
import DaySummary, {
  type DepartmentOption,
} from 'events/components/DaySummary/DaySummary';
import SearchModal from 'events/components/SearchModal';
import { Event, EventTimelineNote } from 'events/_types';
import { useEvents } from 'events/hooks/useEvents';
import { EMPTY_FILTERS } from 'events/components/FilterGroupingControl/_types';
import buildEventsListQuery, {
  dropUnavailableCameraFilters,
} from 'events/helpers/eventFilters';
import useEventGroups from 'events/hooks/useEventGroups';
import useUnseenEventUpdates from 'events/hooks/useUnseenEventUpdates';
import usePreserveScrollOnPrepend from 'events/hooks/usePreserveScrollOnPrepend';
import { useHandover } from 'events/hooks/useHandover';
import { useUser } from 'shared/hooks/api/user';
import { useDepartments } from 'shared/hooks/api/departments';
import { useSignals } from 'shared/hooks/api/signals';
import useDaySummaryState from 'events/hooks/useDaySummaryState';
import useEventsSelection from 'events/hooks/useEventsSelection';
import useEventFilterOptions from 'events/hooks/useEventFilterOptions';
import useInlinePriorityGroups from 'events/hooks/useInlinePriorityGroups';
import useEventCounts from 'events/hooks/useEventCounts';
import useNoteCaptureNavigation from 'events/hooks/useNoteCaptureNavigation';
import useEventsHomeUrlState from 'events/hooks/useEventsHomeUrlState';
import useSearchModal from 'events/hooks/useSearchModal';
import formatDateParam from 'events/helpers/dateFormat';
import {
  InlinePrioritiesCarousel,
  InlinePrioritiesSummary,
  InlinePriorityItem,
} from 'priorities/components/InlinePriorities/_index';
import { useGuidanceQuestions } from 'priorities/hooks/useGuidanceQuestions';
import { usePosthogCapture } from 'events/posthogCapture/_index';
import { readStoredInputMethod } from 'settings/utils/inputMethodStorage';

import * as Styled from 'events/styles/EventsHomePage.style';

const MIN_HANDOVER_NOTES = 3;

function MobilePrioritiesCarousel({
  items,
  onViewAll,
  onEventSelect,
}: {
  items: InlinePriorityItem[];
  onViewAll: () => void;
  onEventSelect: (
    eventId: string,
    options?: { autoStartRecordingForQuestionId?: string }
  ) => void;
}): JSX.Element {
  const { onRecordingToggle, openWritingMode } = useShiftNoteCapture();
  return (
    <InlinePrioritiesCarousel
      items={items}
      onViewAll={onViewAll}
      onRecord={(item) => {
        if (item.eventId) {
          onEventSelect(item.eventId, {
            autoStartRecordingForQuestionId: item.id,
          });
        } else if (readStoredInputMethod() === 'keyboard') {
          openWritingMode(item.id);
        } else {
          onRecordingToggle(item.id);
        }
      }}
    />
  );
}

export default function EventsHomePage(): JSX.Element {
  const history = useHistory();
  const { t } = useTranslation('pageEvents');
  const capture = usePosthogCapture();
  const [windowWidth] = useWindowSize();
  const isDesktopView = windowWidth > parseInt(breakpoints.laptop, 10);

  const allDepartments: DepartmentOption = useMemo(
    () => ({ id: 'all', label: t('eventsHome.allDepartments') }),
    [t]
  );

  const { data: departmentsData } = useDepartments();
  const departmentOptions = useMemo<DepartmentOption[]>(
    () => [
      allDepartments,
      ...(departmentsData ?? []).map((dept) => ({
        id: dept.id,
        label: dept.name,
      })),
    ],
    [allDepartments, departmentsData]
  );

  const {
    selectedDate,
    filters: requestedFilters,
    grouping,
    setSelectedDate,
    setFilters,
    setGrouping,
  } = useEventsHomeUrlState();

  const { data: signalsData, isPending: signalsPending } = useSignals();
  const signals = useMemo(() => signalsData ?? [], [signalsData]);
  const filters = useMemo(
    () =>
      signalsPending
        ? requestedFilters
        : dropUnavailableCameraFilters(requestedFilters, signals),
    [requestedFilters, signals, signalsPending]
  );

  const [selectedDepartment, setSelectedDepartment] =
    useState<DepartmentOption>(allDepartments);
  const [showPriorities, setShowPriorities] = useState(false);

  const {
    summaryExpanded,
    editingPreferences,
    handoverPreferences,
    isSavingPreferences,
    setSummaryExpanded,
    setEditingPreferences,
    handleSavePreferences,
  } = useDaySummaryState();

  const dateParam = useMemo(
    () => formatDateParam(selectedDate),
    [selectedDate]
  );

  const isViewingToday = selectedDate.isSame(dayjs().startOf('day'), 'day');

  const { data, isLoading } = useEvents(dateParam, filters);
  const events = useMemo(() => data?.results ?? [], [data]);

  const listScrollRef = useRef<HTMLDivElement>(null);
  const eventsSignature = useMemo(
    () => events.map((event) => event.id).join(','),
    [events]
  );
  usePreserveScrollOnPrepend(listScrollRef, eventsSignature);

  const { data: currentUser } = useUser();
  const { unseenEventIds, markSeen } = useUnseenEventUpdates(
    events,
    buildEventsListQuery(dateParam, filters),
    data !== undefined,
    currentUser?.id
  );

  const { data: unfilteredEventsData } = useEvents(dateParam);
  const unfilteredEvents = useMemo(
    () => unfilteredEventsData?.results ?? [],
    [unfilteredEventsData]
  );

  const {
    selectedEventId,
    autoStartRecordingForQuestionId,
    setSelectedEventId,
    setAutoStartRecordingForQuestionId,
    handleDateSelect: handleDateSelectFromHook,
    handleEventSelect: handleEventSelectFromHook,
    handleSearchEventSelect: handleSearchEventSelectFromHook,
    handleSearchNoteSelect: handleSearchNoteSelectFromHook,
  } = useEventsSelection({
    events,
    isDesktopView,
    summaryExpanded,
    selectedDate,
    setSelectedDate,
    setSummaryExpanded,
    setEditingPreferences,
  });

  const handleDateSelect = (date: Date) => {
    setShowPriorities(false);
    handleDateSelectFromHook(date);
  };

  const handleEventSelect = (
    eventId: string,
    options?: { autoStartRecordingForQuestionId?: string }
  ) => {
    setShowPriorities(false);
    markSeen(eventId);
    handleEventSelectFromHook(eventId, options);
  };

  const handleSearchEventSelect = (event: Event) => {
    setShowPriorities(false);
    handleSearchEventSelectFromHook(event);
  };

  const handleSearchNoteSelect = (event: Event, note: EventTimelineNote) => {
    setShowPriorities(false);
    handleSearchNoteSelectFromHook(event, note);
  };

  const handleSummaryToggle = (next: boolean) => {
    if (isDesktopView) {
      if (next) {
        const hasPreferences =
          (handoverPreferences?.handover_preferences?.length ?? 0) > 0;
        if (hasPreferences) {
          setSummaryExpanded(true);
          setEditingPreferences(false);
          setShowPriorities(false);
        } else {
          setEditingPreferences(true);
          setSummaryExpanded(false);
          setShowPriorities(false);
        }
      } else {
        setSummaryExpanded(false);
      }
      return;
    }
    setSummaryExpanded(next);
  };

  const handleViewAllPriorities = () => {
    if (isDesktopView) {
      setSummaryExpanded(false);
      setEditingPreferences(false);
      setSelectedEventId(null);
      setShowPriorities(true);
    } else {
      history.push({
        pathname: routesConfig.PRIORITIES_PAGE,
        state: { selectedDate: formatDateParam(selectedDate) },
      });
    }
  };

  const filterOptions = useEventFilterOptions(
    unfilteredEvents,
    departmentsData,
    signals,
    t
  );

  const eventGroups = useEventGroups(events, grouping, t);

  const hasAnyNotes = currentUser?.has_shift_notes ?? false;
  const hasEventsHiddenByFilters =
    Object.values(filters).some((value) => value !== null) &&
    unfilteredEvents.length > 0;

  const { startAt, endAt } = useMemo(() => {
    const start = selectedDate.startOf('day');
    return {
      startAt: start.toISOString(),
      endAt: start.add(1, 'day').toISOString(),
    };
  }, [selectedDate]);

  const handoverNoteCount = useMemo(
    () =>
      unfilteredEvents.reduce(
        (total, event) => total + event.shift_notes_count,
        0
      ),
    [unfilteredEvents]
  );

  const { data: handover } = useHandover({
    startAt,
    endAt,
    departmentId:
      selectedDepartment.id === 'all' ? undefined : selectedDepartment.id,
    enabled: handoverNoteCount >= MIN_HANDOVER_NOTES,
  });

  const summaryShareEmail = useMemo(() => {
    if (!handover?.content) return undefined;
    return {
      subject: t('daySummary.shareEmail.subject', {
        date: selectedDate.format('MMM D, YYYY'),
      }),
      body: handover.content,
    };
  }, [handover?.content, selectedDate, t]);

  const { data: guidanceQuestions } = useGuidanceQuestions();
  const { routineQuestions, eventUpdateQuestions, carouselItems } =
    useInlinePriorityGroups(guidanceQuestions, selectedDate, unfilteredEvents);

  const summaryFilters = useMemo(
    () => ({
      ...EMPTY_FILTERS,
      department:
        selectedDepartment.id === 'all' ? null : selectedDepartment.id,
    }),
    [selectedDepartment.id]
  );
  const { data: summaryEventsData } = useEvents(dateParam, summaryFilters);
  const summaryEvents = useMemo(
    () => summaryEventsData?.results ?? [],
    [summaryEventsData]
  );
  const { resolvedCount, openCount } = useEventCounts(summaryEvents);

  const { handleNotesCreated } = useNoteCaptureNavigation({
    events,
    isDesktopView,
    selectedDate,
    setSummaryExpanded,
    setEditingPreferences,
    setShowPriorities,
  });

  const searchModal = useSearchModal();

  const searchCalendarPill = (
    <DateSearchPill
      selectedDate={selectedDate}
      onSelectDate={handleDateSelect}
      onSearchClick={searchModal.open}
      onCalendarToggle={capture.eventsHomeCalendarClicked}
    />
  );

  const editingInRightColumn = editingPreferences && isDesktopView;
  const editingInSidebar = editingPreferences && !isDesktopView;
  const summaryExpandedInRightColumn = summaryExpanded && isDesktopView;
  const prioritiesInRightColumn = showPriorities && isDesktopView;

  const summarySection = (
    <Styled.SidebarSection>
      <DaySummary
        title={t('eventsHome.summaryTitle')}
        selectedDepartment={selectedDepartment}
        departments={departmentOptions}
        resolvedCount={resolvedCount}
        openCount={openCount}
        summary={handover?.content}
        expanded={isDesktopView ? false : summaryExpanded}
        editingPreferences={editingInSidebar}
        initialPreferences={handoverPreferences?.handover_preferences ?? []}
        isSavingPreferences={isSavingPreferences}
        onToggleExpanded={handleSummaryToggle}
        onDepartmentChange={setSelectedDepartment}
        shareEmail={summaryShareEmail}
        onEditPreferences={() => setEditingPreferences(true)}
        onSavePreferences={handleSavePreferences}
      />
    </Styled.SidebarSection>
  );

  const isEventCardSelected = (eventId: string) =>
    isDesktopView &&
    selectedEventId === eventId &&
    !summaryExpanded &&
    !editingPreferences &&
    !showPriorities;

  const activitySection = (
    <ActivityList
      filters={filters}
      grouping={grouping}
      filterOptions={filterOptions}
      onFiltersChange={setFilters}
      onGroupingChange={setGrouping}
      events={events}
      eventGroups={eventGroups}
      isLoading={isLoading}
      hasAnyNotes={hasAnyNotes}
      hasEventsHiddenByFilters={hasEventsHiddenByFilters}
      isDesktopView={isDesktopView}
      isViewingToday={isViewingToday}
      isEventSelected={isEventCardSelected}
      onEventSelect={handleEventSelect}
      unseenEventIds={unseenEventIds}
    />
  );

  const prioritiesSection = isDesktopView ? (
    <InlinePrioritiesSummary
      routineItems={routineQuestions.map((item) => item.prompt)}
      eventUpdateItems={eventUpdateQuestions.map((item) => item.prompt)}
      onViewAll={handleViewAllPriorities}
      isActive={prioritiesInRightColumn}
    />
  ) : (
    <MobilePrioritiesCarousel
      items={carouselItems}
      onViewAll={handleViewAllPriorities}
      onEventSelect={handleEventSelect}
    />
  );

  return (
    <ErrorBoundary>
      <ShiftNoteCaptureProvider onNotesCreated={handleNotesCreated}>
        <Styled.PageWrapper>
          {!isDesktopView && (
            <TopBar
              rightSlot={searchCalendarPill}
              showBackButton
              backButtonAriaLabel={t('eventsHome.backAriaLabel')}
            />
          )}
          <Styled.SplitLayout>
            <EventsHomeLeftColumn
              isDesktopView={isDesktopView}
              selectedDate={selectedDate}
              onSelectDate={handleDateSelect}
              searchCalendarPill={searchCalendarPill}
              summarySection={summarySection}
              prioritiesSection={prioritiesSection}
              activitySection={activitySection}
              scrollRef={listScrollRef}
            />
            {isDesktopView && (
              <EventsRightColumn
                editingPreferences={editingInRightColumn}
                summaryExpanded={summaryExpandedInRightColumn}
                showPriorities={prioritiesInRightColumn}
                selectedEventId={selectedEventId}
                autoStartRecordingForQuestionId={
                  autoStartRecordingForQuestionId
                }
                onAutoStartRecordingHandled={() =>
                  setAutoStartRecordingForQuestionId(null)
                }
                onEventHidden={() => setSelectedEventId(null)}
                handoverPreferences={handoverPreferences}
                isSavingPreferences={isSavingPreferences}
                onSavePreferences={handleSavePreferences}
                onEditPreferences={() => setEditingPreferences(true)}
                selectedDepartment={selectedDepartment}
                departmentOptions={departmentOptions}
                resolvedCount={resolvedCount}
                openCount={openCount}
                handoverContent={handover?.content}
                summaryShareEmail={summaryShareEmail}
                onDepartmentChange={setSelectedDepartment}
                onEventSelect={handleEventSelect}
                selectedDate={selectedDate}
              />
            )}
          </Styled.SplitLayout>
          {!isDesktopView && isViewingToday && (
            <NoteCaptureSection mobileView />
          )}
        </Styled.PageWrapper>
        <SearchModal
          isOpen={searchModal.isOpen}
          onClose={searchModal.close}
          onEventSelect={handleSearchEventSelect}
          onNoteSelect={handleSearchNoteSelect}
        />
      </ShiftNoteCaptureProvider>
    </ErrorBoundary>
  );
}
